/**
 * The session Home's Today card names when nothing is left to teach today:
 * the next lesson or class on the calendar.
 *
 * Mirrored in mobile/src/lib/nextTeaching.js so both clients agree on which
 * session that is. Rows are teaching-view occurrences (`/scheduler/teaching-today`,
 * `/scheduler/teaching-next`) or, with the Scheduler view off, the legacy
 * `/schedule` occurrences, which carry a local day and time but no instant.
 */

const DAY_MS = 86400000;

/** Who covers a row, from either shape the API sends it in. */
function coverOf(row) {
  return row?.covered || row?.covered_by || null;
}

/**
 * Whether a session is still this instructor's to teach: not cancelled, and
 * not handed to a substitute. One they cover for somebody else counts.
 * @param {Object} row
 * @returns {boolean}
 */
export function teaches(row) {
  if (!row || row.status === 'cancelled') return false;
  const cover = coverOf(row);
  return !cover || cover.mine === true;
}

/** Parse a YYYY-MM-DD as local midnight; `new Date('2026-11-03')` is UTC. */
function localDay(ymd) {
  const [y, m, d] = String(ymd || '').split('-').map(Number);
  return y && m && d ? new Date(y, m - 1, d) : null;
}

/**
 * When a session starts, in epoch milliseconds: its instant when the server
 * sent one, otherwise its local day and time. NaN when neither is readable.
 * @param {Object} row
 * @returns {number}
 */
export function startsAt(row) {
  if (row?.start_at) {
    // Hermes reads at most three fractional digits.
    const t = Date.parse(String(row.start_at).replace(/\.(\d{3})\d+/, '.$1'));
    if (!Number.isNaN(t)) return t;
  }
  const day = localDay(row?.effective_date || row?.date);
  if (!day) return NaN;
  const [h, m] = String(row.time_local || '00:00').split(':').map(Number);
  day.setHours(h || 0, m || 0, 0, 0);
  return day.getTime();
}

/**
 * Whether any of these sessions is still ahead or under way at `now`. A
 * lesson in progress keeps the day open; one that has finished does not.
 * @param {Array<Object>} rows
 * @param {number} now - Epoch milliseconds.
 * @returns {boolean}
 */
export function anyStillAhead(rows, now) {
  return (rows || []).some((row) => {
    if (!teaches(row)) return false;
    const start = startsAt(row);
    return Number.isFinite(start) && start + (Number(row.duration_minutes) || 0) * 60000 > now;
  });
}

/**
 * The first session after today that is still this instructor's to teach.
 * @param {Array<Object>} rows
 * @param {number} now - Epoch milliseconds.
 * @param {string} todayIso - The instructor's local day, YYYY-MM-DD.
 * @returns {Object|null}
 */
export function nextSession(rows, now, todayIso) {
  return (rows || [])
    .filter((row) => teaches(row) && (row.effective_date || row.date) > todayIso && startsAt(row) >= now)
    .sort((a, b) => startsAt(a) - startsAt(b))[0] || null;
}

/**
 * The Today card's business filter: everything, the instructor's own
 * (assigned) lessons, or one organization's.
 * @param {Object} row
 * @param {string} filter - 'all', 'personal' or an org id.
 * @returns {boolean}
 */
export function inBusiness(row, filter) {
  if (filter === 'all') return true;
  if (filter === 'personal') return !row.org_id && row.assignment_status !== 'unassigned';
  return row.org_id === filter;
}

/**
 * "Tomorrow", or null when the session is further off and the caller names
 * the date itself.
 * @param {Object} row
 * @param {string} todayIso
 * @returns {string|null}
 */
export function tomorrowWord(row, todayIso) {
  const day = localDay(row?.effective_date || row?.date);
  const today = localDay(todayIso);
  if (!day || !today) return null;
  return Math.round((day - today) / DAY_MS) === 1 ? 'Tomorrow' : null;
}

/** "Next class" for a class meeting, otherwise "Next lesson". */
export function nextHeading(row) {
  return row?.class_id ? 'Next class' : 'Next lesson';
}
